import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { Store } from 'vuex'
import RootState from '../../store/state'
import { CategoryData } from '../../store/manager/state'
import moment, { Moment } from 'moment'
import 'moment/locale/pt-br'
import { NO_EXPIRE, OLDEST_DATE, PublishConfigData } from './PublishConfigInterface'
import { PublishStatus } from '../../api/gerbera/core'
import { DatePickerOptions } from 'element-ui/types/date-picker'

// 日本時間に設定
moment.locale('ja')

@Component({
  props: {
    articleData: {
      type: Object, /* PublishConfigData */
      required: true
    }
  }
})
export default class PublishConfig extends Vue {
  name: 'PublishConfig'
  $store: Store<RootState>
  readonly articleData: PublishConfigData

  now: Moment = moment()
  categoryId: number | null = null
  hasPublishedDate: boolean = false
  hasExpiresDate: boolean = false
  isPublished: boolean = false

  publishedDateTime: Date = new Date()
  expiresDateTime: Date = new Date()

  pickerOptions: DatePickerOptions = {
    shortcuts: [{
      text: '今日',
      onClick(picker) {
        picker.$emit('pick', moment().toDate())
      }
    }, {
      text: '明日',
      onClick(picker) {
        const date = moment().add(1, 'days')
        picker.$emit('pick', date.toDate())
      }
    }, {
      text: '1週間後',
      onClick(picker) {
        const date = moment().add(1, 'week')
        picker.$emit('pick', date.toDate())
      }
    }, {
      text: '1ヶ月後',
      onClick(picker) {
        const date = moment().add(1, 'month')
        picker.$emit('pick', date.toDate())
      }
    }, {
      text: '1年後',
      onClick(picker) {
        const date = moment().add(1, 'year')
        picker.$emit('pick', date.toDate())
      }
    }]
  }


  get categoryData(): CategoryData[] {
    return this.$store.state.manager.categoryData
  }

  get publishedDate(): Moment {
    if (!this.isPublished) {
      return OLDEST_DATE
    }
    if (this.hasPublishedDate) {
      return moment(this.publishedDateTime)
    }
    return moment()
  }

  get expiresDate(): Moment {
    if (!this.isPublished) {
      return OLDEST_DATE
    }
    if (this.hasExpiresDate) {
      return moment(this.expiresDateTime)
    }
    return NO_EXPIRE
  }

  get status(): PublishStatus {
    if (!this.isPublished) {
      return 'pending'
    }
    if (this.publishedDate.isAfter(moment())) {
      return 'reserved'
    }
    return 'published'
  }

  created(): void {
    this.initializeForm()
  }

  @Watch('articleData')
  initializeForm(): void {
    const article = this.articleData
    this.now = moment()

    // フォームに初期値を代入する
    this.categoryId = article.category.categoryId
    this.initializePublishedDate()
    this.initializeExpiresDate()

    this.isPublished = (article.status !== 'pending')
  }

  initializePublishedDate(): void {
    const published = this.getInitialPublishedDate(this.articleData)
    this.hasPublishedDate = !!published

    const inputPublished = published || this.now
    this.publishedDateTime = moment(inputPublished).toDate()
  }

  initializeExpiresDate(): void {
    const expires = this.getInitialExpiresDate(this.articleData)
    this.hasExpiresDate = !!expires

    const inputExpires = expires || this.now
    this.expiresDateTime = moment(inputExpires).toDate()
  }

  getInitialPublishedDate(source: PublishConfigData): Moment | undefined {
    const published = this.toMoment(source.publishedDate)
    return (published && this.isEffectiveDate(published)) ? published : undefined
  }

  getInitialExpiresDate(source: PublishConfigData): Moment | undefined {
    // 期限切れの場合、非公開とみなす。期限未設定として、再度期限を設定させる。
    const expires = this.toMoment(source.expiresDate)
    return (expires && this.isEffectiveDate(expires) && this.now.isBefore(expires)) ? expires : undefined
  }

  submitEvent(): void {
    const submitConfigData: PublishConfigData = {
      category: {
        categoryId: this.categoryId
      },
      status: this.status,
      publishedDate: this.publishedDate.format(),
      expiresDate: this.expiresDate.format()
    }
    this.$emit('submit', submitConfigData)
  }

  close(): void {
    this.$emit('close')
  }

  toMoment(date: string | Date | undefined): Moment | undefined {
    return date ? moment(date) : undefined
  }

  isEffectiveDate(moment: Moment): boolean {
    return (!!moment && !this.isOldest(moment) && !this.isNoExpire(moment))
  }

  isOldest(moment: Moment): boolean {
    return moment.year() <= 1900
  }

  isNoExpire(moment: Moment): boolean {
    return moment.year() >= 9999
  }

  trackCategory(): void {
  }

  trackIsPublished(): void {
    const label = this.isPublished ? '公開' : '非公開'
  }

  trackHasPublishedDate(): void {
    const label = this.hasPublishedDate ? '公開日を指定' : '今すぐ公開'
  }

  trackHasExpiresDate(): void {
    const label = this.hasExpiresDate ? '掲載終了日を指定' : '期限なし'
  }
}
